import {
  DefaultTransformer,
  deserialize,
  DocumentObject,
  Transformer,
  transform,
} from '@jsonapiworld/jsonapi-serializer';
import { backendApiGateway } from '~/client/backend';
import { RtDeviceSetting, RtDeviceSettingPayload } from '~/types/rt-device-settings';
import { buildJsonAPIQueryParams, jsonAPIFilterBuilder, jsonAPIIncludeBuilder } from '~/utils/jsonapi-query';

const RtDeviceSettingResourceType = 'rt-device-setting';

interface TransformPayload {
  counterpartId: string;
  description?: string | null;
  vatRateAccounts?: Record<string, string> | null;
  vatBook?: { id: string } | null;
  defaultSalesAccount?: { id: string } | null;
}

class RtDeviceSettingTransformer extends Transformer<TransformPayload, {}> {
  constructor(hasVatBook: boolean, hasDefaultSalesAccount: boolean) {
    super();
    this.type = RtDeviceSettingResourceType;
    this.relationships = {
      counterpart: ({ counterpartId }) =>
        transform().withInput({ id: counterpartId }).withTransformer(new DefaultTransformer('counterpart')).toContext(),
    };
    if (hasVatBook) {
      this.relationships.vatBook = (payload: TransformPayload) =>
        transform()
          .withInput(payload.vatBook)
          .withTransformer(new DefaultTransformer('vat-book'))
          .withIncluded(false)
          .toContext();
    }
    if (hasDefaultSalesAccount) {
      this.relationships.defaultSalesAccount = (payload: TransformPayload) =>
        transform()
          .withInput(payload.defaultSalesAccount)
          .withTransformer(new DefaultTransformer('bookkeeping-account'))
          .withIncluded(false)
          .toContext();
    }
  }

  transform({ counterpartId: _, vatBook: __, defaultSalesAccount: ___, ...rest }: TransformPayload) {
    return rest;
  }
}

export const fetchRtDeviceSettings = async (companyId: string): Promise<RtDeviceSetting[]> => {
  const filterBuilder = jsonAPIFilterBuilder();
  filterBuilder.eq('company.id', companyId);
  const includeBuilder = jsonAPIIncludeBuilder(['vatBook', 'defaultSalesAccount', 'counterpart']);
  const searchParams = buildJsonAPIQueryParams(filterBuilder, includeBuilder);

  const { data } = await backendApiGateway.get<DocumentObject>('/api/v1/rt-device-settings', {
    params: searchParams,
  });
  return deserialize<RtDeviceSetting>(data) as RtDeviceSetting[];
};

export const upsertRtDeviceSetting = async (
  counterpartId: string,
  payload: RtDeviceSettingPayload,
): Promise<RtDeviceSetting> => {
  const vatBook = payload.vatBookId !== undefined ? (payload.vatBookId ? { id: payload.vatBookId } : null) : undefined;
  const defaultSalesAccount =
    payload.defaultSalesAccountId !== undefined
      ? payload.defaultSalesAccountId
        ? { id: payload.defaultSalesAccountId }
        : null
      : undefined;
  const transformPayload: TransformPayload = {
    counterpartId,
    description: payload.description,
    vatRateAccounts: payload.vatRateAccounts,
    vatBook,
    defaultSalesAccount,
  };

  const resourceData = transform<TransformPayload, {}>()
    .withInput(transformPayload)
    .withTransformer(
      new RtDeviceSettingTransformer(payload.vatBookId !== undefined, payload.defaultSalesAccountId !== undefined),
    )
    .serialize();

  const { data } = await backendApiGateway.put<DocumentObject>(
    `/api/v1/rt-device-settings/${counterpartId}`,
    resourceData,
  );
  return deserialize<RtDeviceSetting>(data) as RtDeviceSetting;
};
